<template>
  <div class="cn-dashboard">
    <b-card class="mb-4 filter-card">
      <b-row class="align-items-start">
        <b-col lg="5" class="mb-3">
          <label class="font-weight-bold">Khung thời gian</label>
          <div class="d-flex align-items-center">
            <b-form-input v-model="filter.fromDate" type="date" class="mr-2" />
            <span class="mr-2">-</span>
            <b-form-input v-model="filter.toDate" type="date" />
          </div>
          <div class="mt-2">
            <b-button
              v-for="preset in presets"
              :key="preset.key"
              size="sm"
              :variant="activePreset === preset.key ? 'success' : 'outline-secondary'"
              class="mr-2 mb-1"
              @click="applyPreset(preset.key)"
            >
              {{ preset.label }}
            </b-button>
          </div>
        </b-col>
        <b-col lg="3" class="mb-3">
          <label class="font-weight-bold">Chương trình</label>
          <multiselect
            v-model="filter.programs"
            :options="options.programs"
            :multiple="true"
            :close-on-select="false"
            track-by="id"
            label="name"
            placeholder="Tất cả"
            select-label=""
            deselect-label=""
            selected-label=""
          />
        </b-col>
        <b-col lg="2" class="mb-3">
          <label class="font-weight-bold">PO</label>
          <multiselect
            v-model="filter.coaches"
            :options="options.coaches"
            :multiple="true"
            :close-on-select="false"
            track-by="id"
            label="name"
            placeholder="Tất cả"
            select-label=""
            deselect-label=""
            selected-label=""
          />
        </b-col>
        <b-col lg="2" class="mb-3 text-lg-right">
          <label class="font-weight-bold d-none d-lg-block">&nbsp;</label>
          <b-button variant="success" class="mr-2 mb-1" :disabled="loading" @click="loadDashboard">
            <i class="fas fa-sync-alt mr-1"></i>Cập nhật
          </b-button>
          <b-button variant="outline-secondary" class="mb-1" :disabled="loading" @click="resetFilter">
            Xóa bộ lọc
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay :show="loading">
      <b-row>
        <b-col xl="3" md="6" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Khách hàng tham gia theo chương trình</div>
            <div class="kpi-value">{{ summary.totalActive }}</div>
            <div class="kpi-split">
              <div class="d-flex justify-content-between"><span>Khách lẻ</span><b>{{ summary.individual }} ({{ summary.individualPercent }}%)</b></div>
              <b-progress :value="summary.individualPercent" :max="100" height="6px" variant="success" class="mb-2" />
              <div class="d-flex justify-content-between"><span>Doanh nghiệp</span><b>{{ summary.enterprise }} ({{ summary.enterprisePercent }}%)</b></div>
              <b-progress :value="summary.enterprisePercent" :max="100" height="6px" variant="info" />
            </div>
          </div>
        </b-col>
        <b-col xl="3" md="6" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Tổng KH mới</div>
            <div class="kpi-value">{{ summary.newCustomers }}</div>
            <div v-if="summary.newCustomersChangePercent !== null && summary.newCustomersChangePercent !== undefined" :class="summary.newCustomersChangePercent >= 0 ? 'text-success' : 'text-danger'" class="small">
              <i :class="summary.newCustomersChangePercent >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'" class="mr-1"></i>
              {{ Math.abs(summary.newCustomersChangePercent) }}% so với kỳ trước ({{ summary.previousNewCustomers }})
            </div>
            <div v-else class="small text-muted">Kỳ trước không có KH mới để so sánh</div>
          </div>
        </b-col>
        <b-col xl="2" md="4" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Khách hàng hoàn thành</div>
            <div class="kpi-value">{{ summary.completed }}</div>
            <div class="d-flex justify-content-between small"><span>Đạt</span><b class="text-success">{{ summary.passed }} ({{ summary.passedPercent }}%)</b></div>
            <div class="d-flex justify-content-between small"><span>Không đạt</span><b class="text-danger">{{ summary.failed }} ({{ summary.failedPercent }}%)</b></div>
          </div>
        </b-col>
        <b-col xl="2" md="4" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Tạm dừng</div>
            <div class="kpi-value text-warning">{{ summary.paused }} <small class="text-muted">({{ summary.pausedPercent }}%)</small></div>
          </div>
        </b-col>
        <b-col xl="2" md="4" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Từ bỏ</div>
            <div class="kpi-value text-secondary">{{ summary.quit }} <small class="text-muted">({{ summary.quitPercent }}%)</small></div>
          </div>
        </b-col>
        <b-col md="6" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Độ hài lòng (CSAT)</div>
            <div class="kpi-value">
              <template v-if="summary.csatScore !== null && summary.csatScore !== undefined">{{ summary.csatScore }}<small class="text-muted">/5.0</small></template>
              <template v-else>-</template>
            </div>
            <div class="small text-muted">Dựa trên {{ summary.csatCount }} đánh giá bài học &amp; bài tập</div>
          </div>
        </b-col>
        <b-col md="6" class="mb-4">
          <div class="kpi-card">
            <div class="kpi-title">Tỷ lệ tương tác (Engagement)</div>
            <div class="kpi-value">{{ summary.engagementPercent }}% <small class="text-muted">trung bình / khách hàng</small></div>
            <div class="small text-muted">Số ngày khách nhắn tin Zalo / Tổng số ngày của chương trình</div>
          </div>
        </b-col>
      </b-row>

      <b-card no-body>
        <b-tabs v-model="activeTab" card>
          <b-tab v-for="tab in tabs" :key="tab.key" :title="`${tab.label} (${countCustomers(tab.key)})`">
            <div class="table-responsive">
              <table class="table table-bordered mb-0 cn-table">
                <thead>
                  <tr>
                    <th>Khách hàng</th>
                    <th>Tuần hiện tại</th>
                    <th>Chuyên viên phụ trách</th>
                    <th class="text-center">Tình trạng</th>
                    <th class="text-center">Tương tác</th>
                    <th class="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="group in groupsOf(tab.key)">
                    <tr :key="`g-${group.servicePackageId}`" class="group-row">
                      <td colspan="6">Chương trình: {{ group.programName || '-' }}</td>
                    </tr>
                    <tr v-for="row in group.customers" :key="row.userPackageId">
                      <td>
                        {{ row.customerName }}
                        <div v-if="row.onboardGroup" class="small text-muted">{{ row.onboardGroup }}</div>
                      </td>
                      <td>
                        <div class="font-weight-bold text-success">Tuần {{ row.currentWeek }}<span class="text-muted font-weight-normal">/{{ row.totalWeeks }}</span></div>
                        <div class="small text-muted">Bắt đầu: {{ formatDate(row.startDate) }}</div>
                      </td>
                      <td>{{ row.coachName || '-' }}</td>
                      <td class="text-center">
                        <span class="status-badge" :class="statusMeta(row.progressStatus).className">{{ statusMeta(row.progressStatus).label }}</span>
                      </td>
                      <td class="text-center font-weight-bold" :class="engagementClass(row.engagementPercent)">{{ row.engagementPercent }}%</td>
                      <td class="text-center">
                        <b-button size="sm" variant="success" @click="openDetail(row)">Xem chi tiết</b-button>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!groupsOf(tab.key).length">
                    <td colspan="6" class="text-center text-muted py-4">Không có khách hàng phù hợp bộ lọc.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { getDashboardFilters, getDashboard } from './services/services';

const STATUS_META = {
  Passed: { label: 'Đạt', className: 'status-passed' },
  Failed: { label: 'Không đạt', className: 'status-failed' },
  Paused: { label: 'Tạm dừng', className: 'status-paused' },
  Quit: { label: 'Từ bỏ', className: 'status-quit' },
  InProgress: { label: 'Đang cập nhật', className: 'status-in-progress' },
};

const toInputDate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default {
  name: 'CoachNoteDashboard',
  components: { Multiselect },
  data() {
    return {
      loading: false,
      activeTab: 0,
      activePreset: 'thisMonth',
      presets: [
        { key: 'thisWeek', label: 'Tuần này' },
        { key: 'thisMonth', label: 'Tháng này' },
        { key: 'lastWeek', label: 'Tuần trước' },
        { key: 'lastMonth', label: 'Tháng trước' },
      ],
      tabs: [
        { key: 'individual', label: 'Khách lẻ' },
        { key: 'enterprise', label: 'Khách hàng Doanh nghiệp' },
      ],
      options: { programs: [], coaches: [] },
      filter: { fromDate: null, toDate: null, programs: [], coaches: [] },
      summary: {},
      groups: [],
    };
  },
  watch: {
    'filter.fromDate'() {
      this.syncPreset();
    },
    'filter.toDate'() {
      this.syncPreset();
    },
  },
  async mounted() {
    this.applyPreset('thisMonth');
    await this.loadFilterOptions();
    await this.loadDashboard();
  },
  methods: {
    presetRange(key) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const mondayOffset = (today.getDay() + 6) % 7;
      const monday = new Date(today);
      monday.setDate(today.getDate() - mondayOffset);
      if (key === 'thisWeek') {
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return [monday, sunday];
      }
      if (key === 'lastWeek') {
        const start = new Date(monday);
        start.setDate(monday.getDate() - 7);
        const end = new Date(monday);
        end.setDate(monday.getDate() - 1);
        return [start, end];
      }
      if (key === 'lastMonth') {
        return [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)];
      }
      return [new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0)];
    },
    applyPreset(key) {
      const [from, to] = this.presetRange(key);
      this.filter.fromDate = toInputDate(from);
      this.filter.toDate = toInputDate(to);
      this.activePreset = key;
    },
    syncPreset() {
      const match = this.presets.find(p => {
        const [from, to] = this.presetRange(p.key);
        return toInputDate(from) === this.filter.fromDate && toInputDate(to) === this.filter.toDate;
      });
      this.activePreset = match ? match.key : null;
    },
    resetFilter() {
      this.filter.programs = [];
      this.filter.coaches = [];
      this.applyPreset('thisMonth');
      this.loadDashboard();
    },
    async loadFilterOptions() {
      try {
        const res = await getDashboardFilters();
        const data = res?.data ?? res ?? {};
        this.options = { programs: data.programs || [], coaches: data.coaches || [] };
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải bộ lọc.' });
      }
    },
    async loadDashboard() {
      this.loading = true;
      try {
        const res = await getDashboard({
          fromDate: this.filter.fromDate || undefined,
          toDate: this.filter.toDate || undefined,
          servicePackageIds: this.filter.programs.map(x => x.id),
          coachIds: this.filter.coaches.map(x => x.id),
        });
        const data = res?.data ?? res ?? {};
        this.summary = data.summary || {};
        this.groups = data.groups || [];
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể tải dashboard.' });
      } finally {
        this.loading = false;
      }
    },
    groupsOf(tabKey) {
      const isEnterprise = tabKey === 'enterprise';
      return this.groups
        .map(g => ({ ...g, customers: (g.customers || []).filter(c => !!c.isEnterprise === isEnterprise) }))
        .filter(g => g.customers.length);
    },
    countCustomers(tabKey) {
      return this.groupsOf(tabKey).reduce((sum, g) => sum + g.customers.length, 0);
    },
    statusMeta(status) {
      return STATUS_META[status] || STATUS_META.InProgress;
    },
    engagementClass(value) {
      if (value >= 60) return 'text-success';
      if (value >= 40) return 'text-warning';
      return 'text-danger';
    },
    formatDate(value) {
      if (!value) return '';
      const date = /^\d+$/.test(String(value)) ? new Date(Number(value) * 1000) : new Date(value);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('vi-VN');
    },
    openDetail(row) {
      const phone = row.phoneNumber?.replace(/^\+84/, '0') ?? '';
      this.$router.push({
        name: 'coach_note_detail',
        params: { userPackageId: row.userPackageId, phoneNumber: phone },
      });
    },
  },
};
</script>

<style scoped>
.kpi-card {
  height: 100%;
  background: #fff;
  border: 1px solid #EBEDF3;
  border-radius: 10px;
  padding: 16px 18px;
}

.kpi-title {
  color: #3F4254;
  font-weight: 600;
  margin-bottom: 8px;
}

.kpi-value {
  font-size: 26px;
  font-weight: 700;
  color: #01645A;
  margin-bottom: 6px;
}

.kpi-value small {
  font-size: 13px;
  font-weight: 400;
}

.kpi-split {
  font-size: 13px;
}

.cn-table th {
  background: #F3F6F9;
  white-space: nowrap;
}

.group-row td {
  background: #F9FAFB;
  color: #01645A;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 3px 14px;
  border-radius: 20px;
  border: 1px solid;
  font-size: 13px;
  white-space: nowrap;
}

.status-in-progress {
  color: #1E5BB8;
  background: #EAF2FF;
  border-color: #6C9EEB;
}

.status-paused {
  color: #A15C00;
  background: #FFF4DE;
  border-color: #F0B44C;
}

.status-quit {
  color: #5E6278;
  background: #F3F6F9;
  border-color: #B5B5C3;
}

.status-passed {
  color: #1B7A3A;
  background: #E8FFF3;
  border-color: #50CD89;
}

.status-failed {
  color: #B42318;
  background: #FFF5F8;
  border-color: #F1416C;
}
</style>
